import type React from 'react';
import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import { compose } from 'recompose';

import SpecializationsQuery from 'bundles/s12n-common/api/OnDemandSpecialization.graphql';
import type {
  OnDemandSpecialization_SpecializationFragment as OnDemandSpecialization,
  OnDemandSpecializationQuery,
  OnDemandSpecializationQueryVariables,
} from 'bundles/s12n-common/api/__generated__/OnDemandSpecialization';

export type InputProps = {
  s12nIds: string[];
};

export type Specialization = OnDemandSpecialization;
export type Specializations = OnDemandSpecialization[];

export type PropsFromSpecializationQuery = {
  loading: boolean;
  specializations?: OnDemandSpecialization[];
};

export const useSpecializationsData = ({ s12nIds }: { s12nIds: string[] }) => {
  const { loading, data, error } = useQuery<OnDemandSpecializationQuery, OnDemandSpecializationQueryVariables>(
    SpecializationsQuery,
    {
      variables: { s12nIds },
      context: { clientName: 'gatewayGql' },
    }
  );

  const specializations = data?.Specialization?.queryByIds || [];

  return {
    loading,
    error,
    specializations,
  };
};

const withSpecializationsData = <PropsFromCaller extends InputProps>(
  WrappedComponent: React.ComponentType<PropsFromCaller & PropsFromSpecializationQuery>
) =>
  compose<PropsFromCaller & PropsFromSpecializationQuery, PropsFromCaller>(
    graphql<
      PropsFromCaller,
      OnDemandSpecializationQuery,
      OnDemandSpecializationQueryVariables,
      PropsFromSpecializationQuery
    >(SpecializationsQuery, {
      options: ({ s12nIds }) => ({
        variables: {
          s12nIds,
        },
        context: { clientName: 'gatewayGql' },
      }),
      props: ({ data }) => ({
        loading: data?.loading || false,
        specializations: data?.Specialization?.queryByIds || [],
      }),
    })
  )(WrappedComponent);

export default withSpecializationsData;
